import { useState, useEffect } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { api } from "../lib/api";
import { TierSelect } from "../components/onboarding/TierSelect";
import { WellnessAssessment, type AssessmentResponses } from "../components/onboarding/WellnessAssessment";
import { ConsentScreen } from "../components/onboarding/ConsentScreen";

type Props = {
  session: Session;
  member: any;
  onComplete: () => void;
};

type Step = "welcome" | "profile" | "tier" | "assessment" | "consents" | "complete";

export default function OnboardingPage({ session, member, onComplete }: Props) {


  const [step, setStep] = useState<Step>(member.first_name && member.first_name !== "" ? "welcome" : "profile");
  const [firstName, setFirstName] = useState(member.first_name ?? "");
  const [lastName, setLastName] = useState(member.last_name ?? "");
  const [phone, setPhone] = useState(member.phone ?? "");
  const [tier, setTier] = useState<"root" | "rise" | "radiate">(member.tier ?? "root");
  const [assessment, setAssessment] = useState<AssessmentResponses | null>(null);
  const [consents, setConsents] = useState<{ lifescribe_basic: boolean; wearable_integration: boolean; ai_coaching: boolean }>({
    lifescribe_basic: true,
    wearable_integration: true,
    ai_coaching: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add("light");
    return () => { document.body.classList.remove("light"); };
  }, []);


  const stepNum = ({ welcome: 1, profile: 2, tier: 3, assessment: 4, consents: 5, complete: 6 } as const)[step];

  const finish = async () => {
    setSaving(true);
    setError(null);
    try {
      // Auto-detect browser timezone — defaults to America/Phoenix server-side if undefined.
      // Browsers since ~2018 universally support this; falls back gracefully if not.
      let timezone: string | undefined;
      try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { /* noop */ }

      await api.onboarding.complete({
        first_name: firstName,
        last_name: lastName,
        phone: phone || undefined,
        timezone,
        tier,
        assessment_responses: assessment!,
        consents,
      });
      onComplete();
    } catch (e: any) {
      setError(e.message ?? "Something went wrong saving your profile.");
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top progress bar */}
      <header className="border-b border-cardBorder bg-white/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] tracking-[0.32em] text-teal font-medium uppercase">Yvree · LifeScribe</div>
            <div className="text-[10px] tracking-wider text-textBody uppercase">Step {stepNum} of 6</div>
          </div>
          <div className="h-1 bg-sand rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-teal to-tealMuted transition-all duration-500"
              style={{ width: `${(stepNum / 6) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl fade-up">

          {step === "welcome" && (
            <div className="text-center">
              <div className="text-sm tracking-[0.32em] text-teal font-medium uppercase mb-4">Welcome</div>
              <h1 className="font-serif text-5xl text-textPrimary font-light leading-tight mb-6">
                Hello, <span className="italic">{firstName || "friend"}</span>.
              </h1>
              <p className="text-textBody leading-relaxed mb-10 max-w-lg mx-auto">
                LifeScribe is your personal wellness intelligence — an evolving picture of your physical,
                emotional, mental, spiritual, and social health, built from your habits, your reflections,
                and (with your permission) your wearable data.
              </p>
              <p className="text-textBody leading-relaxed mb-10 max-w-lg mx-auto">
                Let's take five minutes to get to know you.
              </p>
              <button
                onClick={() => setStep("profile")}
                className="bg-teal hover:bg-tealDeep text-white tracking-wider uppercase text-xs font-medium px-10 py-3.5 rounded-lg transition"
              >
                Begin
              </button>
            </div>
          )}

          {step === "profile" && (
            <div>
              <div className="text-center mb-8">
                <div className="text-[10px] tracking-[0.32em] text-teal font-medium uppercase mb-3">About you</div>
                <h2 className="font-serif text-4xl text-textPrimary font-light leading-tight">
                  What should we <span className="italic">call you?</span>
                </h2>
              </div>

              <div className="bg-white rounded-2xl border border-cardBorder p-8 space-y-5">
                <label className="block">
                  <span className="text-xs tracking-wider uppercase text-textBody font-medium">First name</span>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="mt-2 w-full px-4 py-3 bg-cream border border-cardBorder rounded-lg text-textPrimary focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 transition"
                    placeholder="Your first name"
                    autoFocus
                  />
                </label>
                <label className="block">
                  <span className="text-xs tracking-wider uppercase text-textBody font-medium">Last name</span>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="mt-2 w-full px-4 py-3 bg-cream border border-cardBorder rounded-lg text-textPrimary focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 transition"
                    placeholder="Your last name"
                  />
                </label>
                <label className="block">
                  <span className="text-xs tracking-wider uppercase text-textBody font-medium">Phone <span className="opacity-50">(optional)</span></span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-2 w-full px-4 py-3 bg-cream border border-cardBorder rounded-lg text-textPrimary focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20 transition"
                    placeholder="For appointment reminders"
                  />
                </label>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setStep("tier")}
                  disabled={!firstName.trim()}
                  className="bg-teal hover:bg-tealDeep disabled:opacity-40 disabled:cursor-not-allowed text-white tracking-wider uppercase text-xs font-medium px-10 py-3.5 rounded-lg transition"
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {step === "tier" && (
            <TierSelect
              value={tier}
              onChange={setTier}
              onNext={() => setStep("assessment")}
              onBack={() => setStep("profile")}
            />
          )}

          {step === "assessment" && (
            <WellnessAssessment
              firstName={firstName}
              onComplete={(responses) => { setAssessment(responses); setStep("consents"); }}
              onBack={() => setStep("tier")}
            />
          )}

          {step === "consents" && (
            <ConsentScreen
              value={consents}
              onChange={setConsents}
              onNext={() => setStep("complete")}
              onBack={() => setStep("assessment")}
            />
          )}

          {step === "complete" && (
            <div className="text-center">
              <div className="text-sm tracking-[0.32em] text-teal font-medium uppercase mb-4">Welcome to LifeScribe</div>
              <h2 className="font-serif text-5xl text-textPrimary font-light leading-tight mb-6">
                You're <span className="italic">ready</span>.
              </h2>
              <p className="text-textBody leading-relaxed mb-4 max-w-md mx-auto">
                We'll compute your first <strong className="text-teal">Yvree Wellness Index</strong> based on
                what you shared. It's a starting picture — it will evolve as you do.
              </p>
              <p className="text-textBody text-sm leading-relaxed mb-10 max-w-md mx-auto opacity-80">
                You can connect a wearable, log daily reflections, and book sessions any time.
              </p>

              {error && (
                <div className="mb-6 p-4 bg-coral/10 border border-coral/30 rounded-lg text-coral text-sm">
                  {error}
                </div>
              )}

              <button
                onClick={finish}
                disabled={saving}
                className="bg-teal hover:bg-tealDeep disabled:opacity-50 text-white tracking-wider uppercase text-xs font-medium px-10 py-3.5 rounded-lg transition"
              >
                {saving ? "Setting up your profile…" : "Open My Dashboard"}
              </button>
            </div>
          )}

        </div>
      </main>

      <footer className="border-t border-cardBorder bg-white/40">
        <div className="max-w-3xl mx-auto px-6 py-3 flex justify-between items-center">
          <button onClick={() => supabase.auth.signOut()} className="text-[10px] tracking-wider uppercase text-textBody hover:text-coral">
            Sign out
          </button>
          <div className="text-[10px] text-textBody opacity-60">{session.user.email}</div>
        </div>
      </footer>
    </div>
  );
}
